import { useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useProjectContracts,
  useContractSovLines,
  useWorkOrderLines,
  useProjectWorkOrders,
} from "@/hooks/useProjectContracts";
import { useSovChangeRequests } from "@/hooks/useSovChangeRequests";
import {
  useUnitSchedule,
  useUnitScopeItems,
  useUnitAssignments,
  useUnitAssignmentDetails,
} from "@/hooks/useUnitSchedule";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useVendors } from "@/hooks/useVendors";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Plus,
  Trash2,
  UserPlus,
  Maximize2,
  Minimize2,
  GripVertical,
  Pencil,
  Clock,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { SovEditDialog } from "./SovEditDialog";
import { cn } from "@/lib/utils";
import { generateWONumber } from "@/lib/naming";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    n
  );
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n);
const fmtQty = (n: number) =>
  n.toLocaleString("en-US", { maximumFractionDigits: 0 });
const pct = (n: number) => `${n.toFixed(1)}%`;

type SovViewMode = "quantity" | "amount" | "both";

interface SovLineForm {
  description: string;
  line_type: string;
  quantity: string;
  unit: string;
  labor_unit_price: string;
  material_unit_price: string;
  sub_unit_price: string;
}

import type { SovLineAlert } from "@/hooks/useSovLineAlerts";

interface Props {
  projectId: string;
  onMutate: () => void;
  sovAlertMap?: Map<string, SovLineAlert[]>;
}

export const ProjectContractTab = ({
  projectId,
  onMutate,
  sovAlertMap,
}: Props) => {
  const isMobile = useIsMobile();
  const { can, isAdmin } = usePermissions();
  const { data: contracts, refetch: refetchContracts } =
    useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines, refetch: refetchSov } = useContractSovLines(
    contract?.id
  );
  const { data: woLines, refetch: refetchWoLines } =
    useWorkOrderLines(projectId);
  const { data: workOrders, refetch: refetchWOs } =
    useProjectWorkOrders(projectId);
  const { data: allVendors } = useVendors();
  const { data: sovChangeRequests, refetch: refetchSovCR } =
    useSovChangeRequests(projectId);
  const { data: unitAssignmentDetails, refetch: refetchUAD } =
    useUnitAssignmentDetails(projectId);
  const { data: scopeItems, refetch: refetchScope } =
    useUnitScopeItems(projectId);
  const { data: unitSchedule } = useUnitSchedule(projectId);

  // Filter vendors to subcontractors only
  const subVendors = useMemo(() => {
    return (allVendors ?? []).filter((v) => {
      const types = v.vendor_types || [v.vendor_type || "general"];
      return types.includes("subcontractor") && !v.is_deleted;
    });
  }, [allVendors]);

  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedSovLine, setSelectedSovLine] = useState<any>(null);
  const [editingSovLine, setEditingSovLine] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [sovViewMode, setSovViewMode] = useState<SovViewMode>("quantity");
  const [expandedSovLines, setExpandedSovLines] = useState<Set<string>>(
    new Set()
  );

  const [contractForm, setContractForm] = useState({
    name: "",
    description: "",
  });
  const [lineItems, setLineItems] = useState<SovLineForm[]>([
    {
      description: "",
      line_type: "labor",
      quantity: "1",
      unit: "SF",
      labor_unit_price: "0",
      material_unit_price: "0",
      sub_unit_price: "0",
    },
  ]);

  const [assignForm, setAssignForm] = useState({
    contractor_id: "",
    description: "",
    quantity: "",
    unit: "",
    contract_rate: "",
  });
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(
    new Set()
  );
  const [overrideReason, setOverrideReason] = useState("");

  const toggleSovExpand = (lineId: string) => {
    setExpandedSovLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const refreshAll = () => {
    refetchContracts();
    refetchSov();
    refetchWoLines();
    refetchWOs();
    refetchSovCR();
    refetchUAD();
    refetchScope();
    onMutate();
  };

  // Unit assignments grouped by sov_line_id
  const unitsBySovLine = useMemo(() => {
    const map: Record<string, any[]> = {};
    (unitAssignmentDetails ?? []).forEach((ua: any) => {
      if (!map[ua.sov_line_id]) map[ua.sov_line_id] = [];
      map[ua.sov_line_id].push(ua);
    });
    return map;
  }, [unitAssignmentDetails]);

  // Unassigned scope items by sov_line_id (has_scope but no assignment)
  const unassignedBySovLine = useMemo(() => {
    const assignedScopeIds = new Set(
      (unitAssignmentDetails ?? []).map((ua: any) => ua.unit_scope_item_id)
    );
    const map: Record<string, any[]> = {};
    (scopeItems ?? []).forEach((si: any) => {
      if (si.has_scope && !assignedScopeIds.has(si.id)) {
        if (!map[si.sov_line_id]) map[si.sov_line_id] = [];
        map[si.sov_line_id].push(si);
      }
    });
    return map;
  }, [scopeItems, unitAssignmentDetails]);

  // Contractor color map for unit chips
  const contractorColors = useMemo(() => {
    const colors = [
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
      "bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200",
      "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200",
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
      "bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200",
      "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
    ];
    const map: Record<string, { color: string; initials: string }> = {};
    let idx = 0;
    (unitAssignmentDetails ?? []).forEach((ua: any) => {
      const key = ua.contractor_name ?? ua.vendor_name ?? "?";
      if (!map[key]) {
        const initials = key
          .split(" ")
          .map((w: string) => w[0]?.toUpperCase())
          .join("")
          .slice(0, 3);
        map[key] = { color: colors[idx % colors.length], initials };
        idx++;
      }
    });
    return map;
  }, [unitAssignmentDetails]);

  // Map pending change requests by sov_line_id
  const pendingBySovLine = useMemo(() => {
    const map = new Map<string, any>();
    (sovChangeRequests ?? []).forEach((cr: any) => {
      if (cr.status === "pending") map.set(cr.sov_line_id, cr);
    });
    return map;
  }, [sovChangeRequests]);

  // Calculate WO cost per SOV line (labor only)
  const woCostBySovLine = useMemo(() => {
    const map: Record<string, number> = {};
    (woLines ?? []).forEach((wol: any) => {
      const wo = wol.work_orders;
      if (
        wo &&
        wo.status !== "rejected" &&
        wo.status !== "cancelled" &&
        wo.status !== "voided" &&
        wo.status !== "closed"
      ) {
        if (wol.status !== "voided" && wol.status !== "released") {
          map[wol.sov_line_id] =
            (map[wol.sov_line_id] || 0) +
            Number(wol.quantity) * Number(wol.contract_rate);
        }
      }
    });
    return map;
  }, [woLines]);

  // Quantity-based aggregation per SOV line
  const sovQtyStats = useMemo(() => {
    const map: Record<
      string,
      {
        assignedQty: number;
        billedQty: number;
        completedQty: number;
        contractors: any[];
      }
    > = {};
    (sovLines ?? []).forEach((line: any) => {
      map[line.id] = {
        assignedQty: 0,
        billedQty: 0,
        completedQty: 0,
        contractors: [],
      };
    });
    // Group WO lines by sov_line_id
    const byContractor: Record<
      string,
      Record<
        string,
        {
          qty: number;
          billedQty: number;
          completedQty: number;
          woNumber: string;
          woId: string;
          contractorName: string;
          woStatus: string;
        }
      >
    > = {};
    (woLines ?? []).forEach((wol: any) => {
      const wo = wol.work_orders;
      if (
        !wo ||
        wo.status === "rejected" ||
        wo.status === "cancelled" ||
        wo.status === "voided" ||
        wo.status === "closed"
      )
        return;
      if (wol.status === "voided" || wol.status === "released") return;
      const sid = wol.sov_line_id;
      if (!map[sid]) return;
      const qty = Number(wol.quantity);
      const billedQty = Number(wol.billed_qty ?? 0);
      const completedQty = Number(
        wol.completed_qty ?? wol.completion_pct
          ? (qty * Number(wol.completion_pct)) / 100
          : 0
      );
      map[sid].assignedQty += qty;
      map[sid].billedQty += billedQty;
      map[sid].completedQty += completedQty;
      if (!byContractor[sid]) byContractor[sid] = {};
      const cKey = wo.contractor_id ?? wo.id;
      if (!byContractor[sid][cKey]) {
        byContractor[sid][cKey] = {
          qty: 0,
          billedQty: 0,
          completedQty: 0,
          woNumber: wo.number,
          woId: wo.id,
          contractorName: wo.vendors?.name ?? "Unknown",
          woStatus: wo.status,
        };
      }
      byContractor[sid][cKey].qty += qty;
      byContractor[sid][cKey].billedQty += billedQty;
      byContractor[sid][cKey].completedQty += completedQty;
    });
    Object.entries(byContractor).forEach(([sid, contractors]) => {
      if (map[sid]) map[sid].contractors = Object.values(contractors);
    });
    return map;
  }, [sovLines, woLines]);

  const contractTotal = useMemo(() => {
    return (sovLines ?? []).reduce((s, l) => s + Number(l.scheduled_value), 0);
  }, [sovLines]);

  // ===== CREATE CONTRACT =====
  const handleCreateContract = async () => {
    if (!contractForm.name) return toast.error("Contract name required");
    const validLines = lineItems.filter((l) => l.description.trim());
    if (!validLines.length) return toast.error("Add at least one line item");

    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) {
      setBusy(false);
      return toast.error("Company not found");
    }

    const totalValue = validLines.reduce((s, l) => {
      const totalUp =
        Number(l.labor_unit_price) + Number(l.material_unit_price);
      return s + Number(l.quantity) * totalUp;
    }, 0);

    const { data: newContract, error } = await supabase
      .from("contracts")
      .insert({
        project_id: projectId,
        company_id: companyId,
        name: contractForm.name,
        description: contractForm.description || null,
        original_value: totalValue,
        revised_value: totalValue,
      })
      .select("id")
      .single();

    if (error) {
      setBusy(false);
      return toast.error(error.message);
    }

    const sovInserts = validLines.map((l, i) => {
      const laborUp = Number(l.labor_unit_price);
      const matUp = Number(l.material_unit_price);
      const totalUp = laborUp + matUp;
      const qty = Number(l.quantity);
      return {
        contract_id: newContract.id,
        company_id: companyId,
        line_number: i + 1,
        description: l.description,
        line_type: l.line_type,
        quantity: qty,
        unit: l.unit,
        unit_price: totalUp,
        labor_unit_price: laborUp,
        material_unit_price: matUp,
        scheduled_value: qty * totalUp,
        sub_unit_price: Number(l.sub_unit_price) || 0,
      };
    });

    const { error: sovErr } = await supabase
      .from("sov_lines")
      .insert(sovInserts);
    if (sovErr) {
      setBusy(false);
      return toast.error(
        "Contract created but SOV lines failed: " + sovErr.message
      );
    }

    setBusy(false);
    toast.success("Contract created");
    setCreateOpen(false);
    setContractForm({ name: "", description: "" });
    setLineItems([
      {
        description: "",
        line_type: "labor",
        quantity: "1",
        unit: "SF",
        labor_unit_price: "0",
        material_unit_price: "0",
        sub_unit_price: "0",
      },
    ]);
    refreshAll();
  };

  // ===== ASSIGN SUB =====
  // Compute available units for selected SOV line
  const sovLineUnitData = useMemo(() => {
    if (!selectedSovLine)
      return {
        hasUnits: false,
        availableUnits: [],
        assignedUnits: [],
        billedUnits: [],
      };
    const lineScope = (scopeItems ?? []).filter(
      (si: any) => si.sov_line_id === selectedSovLine.id && si.has_scope
    );
    if (lineScope.length === 0)
      return {
        hasUnits: false,
        availableUnits: [],
        assignedUnits: [],
        billedUnits: [],
      };

    const assignedScopeIds = new Set(
      (unitAssignmentDetails ?? [])
        .filter((ua: any) => ua.sov_line_id === selectedSovLine.id)
        .map((ua: any) => ua.unit_scope_item_id)
    );
    const billedScopeIds = new Set(
      (unitAssignmentDetails ?? [])
        .filter(
          (ua: any) =>
            ua.sov_line_id === selectedSovLine.id &&
            ua.completion_status === "billed"
        )
        .map((ua: any) => ua.unit_scope_item_id)
    );

    // Map unit_id to unit_number
    const unitMap = new Map((unitSchedule ?? []).map((u: any) => [u.id, u]));

    const available: any[] = [];
    const assigned: any[] = [];
    const billed: any[] = [];

    lineScope.forEach((si: any) => {
      const unit = unitMap.get(si.unit_id);
      const enriched = {
        ...si,
        unit_number: unit?.unit_number ?? "?",
        floor_number: unit?.floor_number,
        building: unit?.building,
      };
      if (billedScopeIds.has(si.id)) {
        billed.push(enriched);
      } else if (assignedScopeIds.has(si.id)) {
        const assignment = (unitAssignmentDetails ?? []).find(
          (ua: any) => ua.unit_scope_item_id === si.id
        );
        assigned.push({
          ...enriched,
          vendor_name:
            assignment?.vendor_name ?? assignment?.contractor_name ?? "Unknown",
          wo_number: assignment?.wo_number,
        });
      } else {
        available.push(enriched);
      }
    });

    // Sort by unit number
    const sortFn = (a: any, b: any) =>
      a.unit_number.localeCompare(b.unit_number, undefined, { numeric: true });
    available.sort(sortFn);
    assigned.sort(sortFn);
    billed.sort(sortFn);

    return {
      hasUnits: true,
      availableUnits: available,
      assignedUnits: assigned,
      billedUnits: billed,
    };
  }, [selectedSovLine, scopeItems, unitAssignmentDetails, unitSchedule]);

  // Auto-compute quantity from selected units
  const unitDerivedQty = useMemo(() => {
    if (!sovLineUnitData.hasUnits || selectedUnitIds.size === 0) return 0;
    return sovLineUnitData.availableUnits
      .filter((u: any) => selectedUnitIds.has(u.id))
      .reduce((s: number, u: any) => s + Number(u.quantity), 0);
  }, [selectedUnitIds, sovLineUnitData]);

  const parseRateError = (msg: string): string | null => {
    if (msg.includes("RATE_EXCEEDS_SELL_RATE"))
      return "Rate must be less than the sell rate — zero-margin subcontractor pricing is not allowed.";
    if (msg.includes("RATE_OVERRIDE_NOT_APPROVED"))
      return "Rate exceeds estimated sub cost and requires approval from an authorized user.";
    return null;
  };

  const handleAssignSub = async () => {
    if (!assignForm.contractor_id) return toast.error("Select a subcontractor");

    const hasUnits = sovLineUnitData.hasUnits;
    const qty = hasUnits ? unitDerivedQty : Number(assignForm.quantity);

    if (qty <= 0)
      return toast.error(
        hasUnits ? "Select at least one unit" : "Enter quantity"
      );
    if (!assignForm.contract_rate || Number(assignForm.contract_rate) <= 0)
      return toast.error("Enter rate");

    const rate = Number(assignForm.contract_rate);
    const laborRate = Number(selectedSovLine.labor_unit_price);
    const subRate = Number(selectedSovLine.sub_unit_price ?? 0);

    // Hard block: rate >= sell rate (zero margin not allowed)
    if (rate >= laborRate)
      return toast.error(
        `Rate must be less than sell rate (${fmtRate(
          laborRate
        )}) — zero-margin pricing is not allowed`
      );

    // Override gate: rate > estimated sub rate
    const isOverride = subRate > 0 && rate > subRate;
    if (isOverride) {
      if (!overrideReason.trim())
        return toast.error(
          `Rate exceeds estimated sub rate (${fmtRate(
            subRate
          )}). Enter a variance reason.`
        );
      if (!can("approve_rate_overrides") && !isAdmin)
        return toast.error(
          "Rate override requires approval from an authorized user (approve_rate_overrides permission)."
        );
    }

    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) {
      setBusy(false);
      return toast.error("Company not found");
    }
    const userId = (await supabase.auth.getUser()).data.user?.id;

    const woTotal = qty * rate;
    const selectedVendor = subVendors.find(
      (v) => v.id === assignForm.contractor_id
    );
    const vendorName = selectedVendor?.name ?? "Unknown";
    const woNumber = await generateWONumber(
      projectId,
      assignForm.contractor_id,
      vendorName
    );

    const overrideFields = isOverride
      ? {
          rate_source: "manual_override",
          rate_override_reason: overrideReason,
          rate_approved_by: userId,
          rate_approved_at: new Date().toISOString(),
        }
      : { rate_source: "estimate" };

    const { data: wo, error: woErr } = await supabase
      .from("work_orders")
      .insert({
        project_id: projectId,
        company_id: companyId,
        contractor_id: assignForm.contractor_id,
        number: woNumber,
        description: assignForm.description || selectedSovLine.description,
        amount: woTotal,
        sov_line_id: selectedSovLine.id,
      })
      .select("id")
      .single();

    if (woErr) {
      setBusy(false);
      return toast.error(woErr.message);
    }

    if (hasUnits && selectedUnitIds.size > 0) {
      const selectedUnits = sovLineUnitData.availableUnits.filter((u: any) =>
        selectedUnitIds.has(u.id)
      );
      const wolInserts = selectedUnits.map((u: any) => ({
        work_order_id: wo.id,
        sov_line_id: selectedSovLine.id,
        company_id: companyId,
        description: `${selectedSovLine.description} — Unit ${u.unit_number}`,
        quantity: Number(u.quantity),
        unit: selectedSovLine.unit,
        contract_rate: rate,
        unit_id: u.unit_id,
        sov_rate: laborRate,
        ...overrideFields,
      }));
      const { error: wolErr } = await supabase
        .from("work_order_lines")
        .insert(wolInserts as any);
      if (wolErr) {
        setBusy(false);
        const friendly = parseRateError(wolErr.message);
        return toast.error(friendly ?? wolErr.message);
      }
    } else {
      const { error: wolErr } = await supabase.from("work_order_lines").insert({
        work_order_id: wo.id,
        sov_line_id: selectedSovLine.id,
        company_id: companyId,
        description: assignForm.description || selectedSovLine.description,
        quantity: qty,
        unit: assignForm.unit || selectedSovLine.unit,
        contract_rate: rate,
        sov_rate: laborRate,
        ...overrideFields,
      });
      if (wolErr) {
        setBusy(false);
        const friendly = parseRateError(wolErr.message);
        return toast.error(friendly ?? wolErr.message);
      }
    }

    // Audit log override
    if (isOverride) {
      await supabase.from("ops_audit_log").insert({
        table_name: "work_order_lines",
        record_id: wo.id,
        action: "wo_rate_override",
        changed_by: userId,
        metadata: {
          sov_line_id: selectedSovLine.id,
          estimated_sub_rate: subRate,
          override_rate: rate,
          reason: overrideReason,
          approver: userId,
        },
      } as any);
    }

    setBusy(false);
    toast.success("Work order created");
    setAssignOpen(false);
    setAssignForm({
      contractor_id: "",
      description: "",
      quantity: "",
      unit: "",
      contract_rate: "",
    });
    setOverrideReason("");
    setSelectedUnitIds(new Set());
    setSelectedSovLine(null);
    refreshAll();
  };

  const addLineItem = () =>
    setLineItems((l) => [
      ...l,
      {
        description: "",
        line_type: "labor",
        quantity: "1",
        unit: "SF",
        labor_unit_price: "0",
        material_unit_price: "0",
        sub_unit_price: "0",
      },
    ]);
  const removeLineItem = (idx: number) =>
    setLineItems((l) => l.filter((_, i) => i !== idx));
  const updateLineItem = (
    idx: number,
    field: keyof SovLineForm,
    value: string
  ) =>
    setLineItems((l) =>
      l.map((li, i) => {
        if (i !== idx) return li;
        const updated = { ...li, [field]: value };
        // Auto-zero hidden price fields based on type
        if (field === "line_type") {
          if (value === "labor") updated.material_unit_price = "0";
          if (value === "materials") updated.labor_unit_price = "0";
        }
        return updated;
      })
    );

  const formLaborTotal = lineItems.reduce(
    (s, l) => s + Number(l.quantity) * Number(l.labor_unit_price),
    0
  );
  const formMaterialTotal = lineItems.reduce(
    (s, l) => s + Number(l.quantity) * Number(l.material_unit_price),
    0
  );
  const formTotal = formLaborTotal + formMaterialTotal;

  const openAssignDialog = (sovLine: any) => {
    setSelectedSovLine(sovLine);
    setAssignForm({
      contractor_id: "",
      description: sovLine.description,
      quantity: String(sovLine.quantity),
      unit: sovLine.unit,
      contract_rate: String(Number(sovLine.sub_unit_price) || ""),
    });
    setSelectedUnitIds(new Set());
    setAssignOpen(true);
  };

  // ===== STATE A: No contract =====
  if (!contract) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold mb-2">No Contract</h3>
          <p className="text-muted-foreground mb-4">
            Create a contract with Schedule of Values to start tracking
            financials.
          </p>
          {can("edit_contract_sov") && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Create Contract
            </Button>
          )}
        </div>

        <CreateContractDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          form={contractForm}
          setForm={setContractForm}
          lineItems={lineItems}
          addLineItem={addLineItem}
          removeLineItem={removeLineItem}
          updateLineItem={updateLineItem}
          formLaborTotal={formLaborTotal}
          formMaterialTotal={formMaterialTotal}
          formTotal={formTotal}
          busy={busy}
          onSubmit={handleCreateContract}
        />
      </div>
    );
  }

  // ===== STATE B: Contract exists =====
  return (
    <div className="space-y-6">
      {/* Contract Header */}
      <Card>
        <CardContent className="pt-5 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Contract
              </p>
              <p className="font-semibold">{contract.name}</p>
            </div>
            <StatusBadge status={contract.status} />
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total Value</p>
            <p className="text-xl font-bold">
              {fmt(Number(contract.revised_value))}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* SOV Lines */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Schedule of Values
          </h3>
          {!isMobile && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">View by:</span>
              <ToggleGroup
                type="single"
                value={sovViewMode}
                onValueChange={(v) => v && setSovViewMode(v as SovViewMode)}
                size="sm"
              >
                <ToggleGroupItem
                  value="quantity"
                  className="text-xs h-7 px-2.5"
                >
                  Quantity
                </ToggleGroupItem>
                <ToggleGroupItem value="amount" className="text-xs h-7 px-2.5">
                  $ Amount
                </ToggleGroupItem>
                <ToggleGroupItem value="both" className="text-xs h-7 px-2.5">
                  Both
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
        </div>

        {isMobile ? (
          /* ── Mobile SOV Card View ── */
          <div className="space-y-3">
            {(sovLines ?? []).map((line: any) => {
              const qty = Number(line.quantity);
              const unitPrice = Number(line.unit_price);
              const sovValue = Number(line.scheduled_value);
              const laborUp = Number(line.labor_unit_price ?? 0);
              const stats = sovQtyStats[line.id] || {
                assignedQty: 0,
                billedQty: 0,
                completedQty: 0,
                contractors: [],
              };
              const billedPct = qty > 0 ? (stats.billedQty / qty) * 100 : 0;
              const completedUnbilledPct =
                qty > 0
                  ? (Math.max(0, stats.completedQty - stats.billedQty) / qty) *
                    100
                  : 0;
              const assignedNotCompletePct =
                qty > 0
                  ? (Math.max(0, stats.assignedQty - stats.completedQty) /
                      qty) *
                    100
                  : 0;
              const woCost = woCostBySovLine[line.id] || 0;
              const pendingCR = pendingBySovLine.get(line.id);

              return (
                <div
                  key={line.id}
                  className={cn(
                    "border rounded-lg p-3 space-y-2",
                    line.source_type === "change_order" &&
                      "border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/30"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {line.description}
                        </span>
                        {line.source_type === "change_order" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 border-green-300 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-400 shrink-0"
                          >
                            CO
                          </Badge>
                        )}
                        {pendingCR && (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 border-yellow-300 text-yellow-600 shrink-0"
                          >
                            <Clock className="h-3 w-3" /> Pending
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {fmtQty(qty)} {line.unit} · {fmtRate(unitPrice)}/
                        {line.unit}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-sm">
                        {fmt(sovValue)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        Committed: {fmt(woCost)}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden flex">
                        {billedPct > 0 && (
                          <div
                            className="h-full bg-emerald-600 dark:bg-emerald-500"
                            style={{ width: `${Math.min(billedPct, 100)}%` }}
                          />
                        )}
                        {completedUnbilledPct > 0 && (
                          <div
                            className="h-full bg-emerald-300 dark:bg-emerald-800"
                            style={{
                              width: `${Math.min(
                                completedUnbilledPct,
                                100 - billedPct
                              )}%`,
                            }}
                          />
                        )}
                        {assignedNotCompletePct > 0 && (
                          <div
                            className="h-full bg-sky-300 dark:bg-sky-800"
                            style={{
                              width: `${Math.min(
                                assignedNotCompletePct,
                                100 - billedPct - completedUnbilledPct
                              )}%`,
                            }}
                          />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground font-medium w-10 text-right">
                        {pct(billedPct)}
                      </span>
                    </div>
                    <div className="flex gap-3 text-[10px] text-muted-foreground">
                      <span>Assigned: {fmtQty(stats.assignedQty)}</span>
                      <span>Billed: {fmtQty(stats.billedQty)}</span>
                      <span>Remaining: {fmtQty(qty - stats.billedQty)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-1">
                    {laborUp > 0 && can("create_work_orders") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => openAssignDialog(line)}
                      >
                        <UserPlus className="mr-1 h-3 w-3" />
                        Assign
                      </Button>
                    )}
                    {can("edit_contract_sov") && !pendingCR && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditingSovLine(line)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Mobile totals */}
            {(sovLines ?? []).length > 0 && (
              <div className="border rounded-lg p-3 bg-muted/30">
                <div className="flex justify-between text-sm font-semibold">
                  <span>Total Contract</span>
                  <span>{fmt(contractTotal)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Committed</span>
                  <span>
                    {fmt(
                      Object.values(woCostBySovLine).reduce((s, v) => s + v, 0)
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <TooltipProvider delayDuration={200}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead className="text-sm">SOV Line</TableHead>
                    <TableHead className="w-16 text-sm">UOM</TableHead>
                    {(sovViewMode === "quantity" || sovViewMode === "both") && (
                      <>
                        <TableHead className="text-right text-sm">
                          Contract Qty
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Assigned Qty
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Billed Qty
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Remaining Qty
                        </TableHead>
                      </>
                    )}
                    {(sovViewMode === "amount" || sovViewMode === "both") && (
                      <>
                        <TableHead className="text-right text-sm">
                          Rate
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Contract $
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Assigned $
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Billed $
                        </TableHead>
                        <TableHead className="text-right text-sm">
                          Remaining $
                        </TableHead>
                      </>
                    )}
                    <TableHead className="w-40 text-sm">Production</TableHead>
                    <TableHead className="w-40 text-sm">Invoiced</TableHead>
                    <TableHead className="text-right w-28 text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(sovLines ?? []).map((line: any, i: number) => {
                    const qty = Number(line.quantity);
                    const unitPrice = Number(line.unit_price);
                    const sovValue = Number(line.scheduled_value);
                    const laborUp = Number(line.labor_unit_price ?? 0);
                    const stats = sovQtyStats[line.id] || {
                      assignedQty: 0,
                      billedQty: 0,
                      completedQty: 0,
                      contractors: [],
                    };
                    const remainingQty = qty - stats.billedQty;
                    const progressPct =
                      qty > 0 ? (stats.billedQty / qty) * 100 : 0;
                    const billedPct =
                      qty > 0 ? (stats.billedQty / qty) * 100 : 0;
                    const completedUnbilledPct =
                      qty > 0
                        ? (Math.max(0, stats.completedQty - stats.billedQty) /
                            qty) *
                          100
                        : 0;
                    const assignedNotCompletePct =
                      qty > 0
                        ? (Math.max(0, stats.assignedQty - stats.completedQty) /
                            qty) *
                          100
                        : 0;
                    const pendingCR = pendingBySovLine.get(line.id);
                    const isExpanded = expandedSovLines.has(line.id);
                    const woCost = woCostBySovLine[line.id] || 0;

                    // Money stats for tooltip and amount view
                    const assignedDollars = woCost;
                    const billedDollars = stats.billedQty * unitPrice;
                    const remainingDollars = sovValue - billedDollars;
                    const unbilledEarnedDollars =
                      Math.max(0, stats.completedQty - stats.billedQty) *
                      unitPrice;

                    return (
                      <>
                        <Tooltip key={line.id}>
                          <TooltipTrigger asChild>
                            <TableRow
                              className={cn(
                                "cursor-pointer",
                                i % 2 === 1 ? "bg-muted/30" : "",
                                line.source_type === "change_order" &&
                                  "bg-green-50 dark:bg-green-950/30"
                              )}
                              onClick={() => toggleSovExpand(line.id)}
                            >
                              <TableCell className="px-2 py-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell className="py-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">
                                    {line.description}
                                  </span>
                                  {line.source_type === "change_order" && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs gap-1 border-green-300 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-400 dark:border-green-800"
                                    >
                                      CO Addendum
                                    </Badge>
                                  )}
                                  {sovViewMode === "quantity" &&
                                    unitPrice > 0 && (
                                      <span className="text-sm text-muted-foreground ml-1">
                                        · {fmtRate(unitPrice)}/{line.unit}
                                      </span>
                                    )}
                                  {pendingCR && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs gap-1 border-yellow-300 text-yellow-600"
                                    >
                                      <Clock className="h-3 w-3" /> Pending
                                    </Badge>
                                  )}
                                  {sovAlertMap?.has(line.id) &&
                                    (() => {
                                      const lineAlerts = sovAlertMap.get(
                                        line.id
                                      )!;
                                      const worst = lineAlerts.some(
                                        (a) => a.severity === "critical"
                                      )
                                        ? "critical"
                                        : lineAlerts.some(
                                            (a) => a.severity === "warning"
                                          )
                                        ? "warning"
                                        : "info";
                                      const dotColor =
                                        worst === "critical"
                                          ? "bg-destructive"
                                          : worst === "warning"
                                          ? "bg-yellow-500"
                                          : "bg-blue-500";
                                      return (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span
                                                className={`inline-block h-2 w-2 rounded-full ${dotColor}`}
                                              />
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="right"
                                              className="max-w-xs"
                                            >
                                              {lineAlerts.map((a, idx) => (
                                                <p
                                                  key={idx}
                                                  className="text-xs"
                                                >
                                                  {a.explanation}
                                                </p>
                                              ))}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })()}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-sm py-3">
                                {line.unit}
                              </TableCell>
                              {(sovViewMode === "quantity" ||
                                sovViewMode === "both") && (
                                <>
                                  <TableCell className="text-right font-medium text-sm py-3">
                                    {fmtQty(qty)} {line.unit}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmtQty(stats.assignedQty)} {line.unit}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmtQty(stats.billedQty)} {line.unit}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmtQty(remainingQty)} {line.unit}
                                  </TableCell>
                                </>
                              )}
                              {(sovViewMode === "amount" ||
                                sovViewMode === "both") && (
                                <>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmtRate(unitPrice)}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-sm py-3">
                                    {fmt(sovValue)}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmt(assignedDollars)}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmt(billedDollars)}
                                  </TableCell>
                                  <TableCell className="text-right text-sm py-3">
                                    {fmt(remainingDollars)}
                                  </TableCell>
                                </>
                              )}
                              <TableCell className="py-3">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-4 rounded-full bg-muted overflow-hidden flex">
                                    {billedPct > 0 && (
                                      <div
                                        className="h-full bg-emerald-600 dark:bg-emerald-500"
                                        style={{
                                          width: `${Math.min(billedPct, 100)}%`,
                                        }}
                                      />
                                    )}
                                    {completedUnbilledPct > 0 && (
                                      <div
                                        className="h-full bg-emerald-300 dark:bg-emerald-800"
                                        style={{
                                          width: `${Math.min(
                                            completedUnbilledPct,
                                            100 - billedPct
                                          )}%`,
                                        }}
                                      />
                                    )}
                                    {assignedNotCompletePct > 0 && (
                                      <div
                                        className="h-full bg-sky-300 dark:bg-sky-800"
                                        style={{
                                          width: `${Math.min(
                                            assignedNotCompletePct,
                                            100 -
                                              billedPct -
                                              completedUnbilledPct
                                          )}%`,
                                        }}
                                      />
                                    )}
                                  </div>
                                  <span className="text-sm text-muted-foreground w-12 text-right font-medium">
                                    {pct(progressPct)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                {(() => {
                                  const invoicedPct =
                                    sovValue > 0
                                      ? (billedDollars / sovValue) * 100
                                      : 0;
                                  return (
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-4 rounded-full bg-muted overflow-hidden flex">
                                        {invoicedPct > 0 && (
                                          <div
                                            className="h-full bg-violet-500 dark:bg-violet-400"
                                            style={{
                                              width: `${Math.min(
                                                invoicedPct,
                                                100
                                              )}%`,
                                            }}
                                          />
                                        )}
                                      </div>
                                      <span className="text-sm text-muted-foreground w-12 text-right font-medium">
                                        {pct(invoicedPct)}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell
                                className="text-right py-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-end gap-1">
                                  {laborUp > 0 && can("create_work_orders") && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 text-sm px-3"
                                      onClick={() => openAssignDialog(line)}
                                    >
                                      <UserPlus className="mr-1 h-4 w-4" />
                                      Assign
                                    </Button>
                                  )}
                                  {can("edit_contract_sov") && !pendingCR && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0"
                                      onClick={() => setEditingSovLine(line)}
                                      title="Edit SOV line"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          </TooltipTrigger>
                          {sovViewMode === "quantity" && (
                            <TooltipContent side="right" className="max-w-xs">
                              <div className="space-y-1 text-xs">
                                <p className="font-semibold text-sm">
                                  {line.description}
                                </p>
                                <div className="border-t border-border pt-1 space-y-0.5">
                                  <div className="flex justify-between gap-6">
                                    <span className="text-muted-foreground">
                                      Rate:
                                    </span>{" "}
                                    <span>
                                      {fmtRate(unitPrice)} / {line.unit}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-6">
                                    <span className="text-muted-foreground">
                                      Contract:
                                    </span>{" "}
                                    <span>{fmt(sovValue)}</span>
                                  </div>
                                  <div className="flex justify-between gap-6">
                                    <span className="text-muted-foreground">
                                      Assigned:
                                    </span>{" "}
                                    <span>{fmt(assignedDollars)}</span>
                                  </div>
                                  <div className="flex justify-between gap-6">
                                    <span className="text-muted-foreground">
                                      Billed:
                                    </span>{" "}
                                    <span>{fmt(billedDollars)}</span>
                                  </div>
                                  <div className="flex justify-between gap-6">
                                    <span className="text-muted-foreground">
                                      Remaining:
                                    </span>{" "}
                                    <span>{fmt(remainingDollars)}</span>
                                  </div>
                                  {unbilledEarnedDollars > 0 && (
                                    <div className="flex justify-between gap-6 text-amber-600 dark:text-amber-400">
                                      <span>Unbilled earned:</span>{" "}
                                      <span>{fmt(unbilledEarnedDollars)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TooltipContent>
                          )}
                        </Tooltip>
                        {/* Expanded breakdown with unit chips + contractor table */}
                        {isExpanded &&
                          (() => {
                            const lineUnits = unitsBySovLine[line.id] ?? [];
                            const lineUnassigned =
                              unassignedBySovLine[line.id] ?? [];
                            const hasUnits =
                              lineUnits.length > 0 || lineUnassigned.length > 0;
                            const totalColSpan =
                              sovViewMode === "both" ? 16 : 11;

                            return (
                              <>
                                <TableRow className="bg-muted/20 border-0">
                                  <TableCell colSpan={totalColSpan}>
                                    <div className="pl-6 py-1 text-xs text-muted-foreground font-medium">
                                      {line.description} · {fmtQty(qty)}{" "}
                                      {line.unit} total · {fmtRate(unitPrice)}/
                                      {line.unit}
                                      {hasUnits && (
                                        <span className="ml-2">
                                          ·{" "}
                                          {lineUnits.length +
                                            lineUnassigned.length}{" "}
                                          units
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {/* Contractor sub-header */}
                                <TableRow className="bg-muted/20 border-0">
                                  <TableCell />
                                  <TableCell className="text-xs font-medium text-muted-foreground">
                                    Contractor
                                  </TableCell>
                                  <TableCell className="text-xs font-medium text-muted-foreground">
                                    WO
                                  </TableCell>
                                  <TableCell className="text-xs font-medium text-muted-foreground text-right">
                                    Alloc Qty
                                  </TableCell>
                                  <TableCell className="text-xs font-medium text-muted-foreground text-right">
                                    Done Qty
                                  </TableCell>
                                  <TableCell className="text-xs font-medium text-muted-foreground text-right">
                                    Billed Qty
                                  </TableCell>
                                  <TableCell className="text-xs font-medium text-muted-foreground text-right">
                                    Remaining
                                  </TableCell>
                                  <TableCell
                                    className="text-xs font-medium text-muted-foreground"
                                    colSpan={totalColSpan - 7}
                                  >
                                    Status
                                  </TableCell>
                                </TableRow>
                                {stats.contractors.map((c: any, ci: number) => {
                                  const cRemaining = c.qty - c.billedQty;
                                  const statusIcon =
                                    c.billedQty >= c.qty
                                      ? "●"
                                      : c.completedQty >= c.qty
                                      ? "◕"
                                      : c.billedQty > 0
                                      ? "◑"
                                      : "◔";
                                  const statusLabel =
                                    c.billedQty >= c.qty
                                      ? "complete"
                                      : c.completedQty >= c.qty
                                      ? "done/unbilled"
                                      : c.billedQty > 0
                                      ? "partial"
                                      : "assigned";
                                  const cInfo =
                                    contractorColors[c.contractorName];
                                  // Count units for this contractor on this SOV line
                                  const cUnits = lineUnits.filter(
                                    (ua: any) =>
                                      (ua.contractor_name ?? ua.vendor_name) ===
                                      c.contractorName
                                  );
                                  return (
                                    <TableRow
                                      key={ci}
                                      className="bg-muted/10 border-0"
                                    >
                                      <TableCell />
                                      <TableCell className="text-sm">
                                        <span className="flex items-center gap-1.5">
                                          {cInfo && (
                                            <span
                                              className={cn(
                                                "inline-block w-2.5 h-2.5 rounded-sm",
                                                cInfo.color
                                              )}
                                            />
                                          )}
                                          {c.contractorName}
                                          {cUnits.length > 0 && (
                                            <span className="text-[10px] text-muted-foreground">
                                              ({cUnits.length} units)
                                            </span>
                                          )}
                                        </span>
                                      </TableCell>
                                      <TableCell className="font-mono text-xs">
                                        {c.woNumber}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {fmtQty(c.qty)} {line.unit}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {fmtQty(c.completedQty)} {line.unit}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {fmtQty(c.billedQty)} {line.unit}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {fmtQty(cRemaining)} {line.unit}
                                      </TableCell>
                                      <TableCell colSpan={totalColSpan - 7}>
                                        <span className="text-sm">
                                          {statusIcon} {statusLabel}
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {/* Unassigned row */}
                                {qty - stats.assignedQty > 0 && (
                                  <TableRow className="bg-muted/10 border-0">
                                    <TableCell />
                                    <TableCell className="text-sm text-muted-foreground italic">
                                      — Unassigned{" "}
                                      {lineUnassigned.length > 0 &&
                                        `(${lineUnassigned.length} units)`}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                      —
                                    </TableCell>
                                    <TableCell className="text-right text-sm">
                                      {fmtQty(qty - stats.assignedQty)}{" "}
                                      {line.unit}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                      —
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                      —
                                    </TableCell>
                                    <TableCell className="text-right text-sm">
                                      {fmtQty(qty - stats.assignedQty)}{" "}
                                      {line.unit}
                                    </TableCell>
                                    <TableCell colSpan={totalColSpan - 7}>
                                      <span className="text-sm text-muted-foreground">
                                        ○
                                      </span>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {/* Subtotals */}
                                <TableRow className="bg-muted/20 border-0 font-medium">
                                  <TableCell />
                                  <TableCell className="text-xs" colSpan={2}>
                                    Totals
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {fmtQty(stats.assignedQty)}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {fmtQty(stats.completedQty)}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {fmtQty(stats.billedQty)}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {fmtQty(remainingQty)}
                                  </TableCell>
                                  <TableCell colSpan={totalColSpan - 7} />
                                </TableRow>
                                {/* Alerts */}
                                {stats.completedQty > stats.billedQty && (
                                  <TableRow className="bg-amber-50 dark:bg-amber-950/30 border-0">
                                    <TableCell />
                                    <TableCell
                                      colSpan={totalColSpan - 1}
                                      className="text-xs text-amber-700 dark:text-amber-300"
                                    >
                                      ⚠ Unbilled complete:{" "}
                                      {fmtQty(
                                        stats.completedQty - stats.billedQty
                                      )}{" "}
                                      {line.unit} ({fmt(unbilledEarnedDollars)})
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            );
                          })()}
                      </>
                    );
                  })}
                </TableBody>
                {/* Footer with totals */}
                {(sovLines ?? []).length > 0 && (
                  <TableFooter>
                    <TableRow className="font-semibold">
                      <TableCell />
                      <TableCell>Totals</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        (mixed)
                      </TableCell>
                      {(sovViewMode === "quantity" ||
                        sovViewMode === "both") && (
                        <>
                          <TableCell className="text-right text-muted-foreground text-xs">
                            (mixed UOM)
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground text-xs">
                            (mixed UOM)
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground text-xs">
                            (mixed UOM)
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground text-xs">
                            (mixed UOM)
                          </TableCell>
                        </>
                      )}
                      {(sovViewMode === "amount" || sovViewMode === "both") && (
                        <>
                          <TableCell />
                          <TableCell className="text-right">
                            {fmt(contractTotal)}
                          </TableCell>
                          <TableCell className="text-right">
                            {fmt(
                              Object.values(woCostBySovLine).reduce(
                                (s, v) => s + v,
                                0
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {fmt(
                              (sovLines ?? []).reduce(
                                (s: number, l: any) =>
                                  s +
                                  (sovQtyStats[l.id]?.billedQty ?? 0) *
                                    Number(l.unit_price),
                                0
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {fmt(
                              (sovLines ?? []).reduce(
                                (s: number, l: any) =>
                                  s +
                                  Number(l.scheduled_value) -
                                  (sovQtyStats[l.id]?.billedQty ?? 0) *
                                    Number(l.unit_price),
                                0
                              )
                            )}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <div className="text-xs">
                          {fmt(
                            (sovLines ?? []).reduce(
                              (s: number, l: any) =>
                                s +
                                (sovQtyStats[l.id]?.billedQty ?? 0) *
                                  Number(l.unit_price),
                              0
                            )
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          {fmt(
                            (sovLines ?? []).reduce(
                              (s: number, l: any) =>
                                s +
                                (sovQtyStats[l.id]?.billedQty ?? 0) *
                                  Number(l.unit_price),
                              0
                            )
                          )}
                        </div>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    {/* UOM subtotals */}
                    {sovViewMode !== "amount" &&
                      (() => {
                        const uomGroups: Record<
                          string,
                          {
                            contract: number;
                            billed: number;
                            remaining: number;
                          }
                        > = {};
                        (sovLines ?? []).forEach((l: any) => {
                          const u = l.unit;
                          if (!uomGroups[u])
                            uomGroups[u] = {
                              contract: 0,
                              billed: 0,
                              remaining: 0,
                            };
                          uomGroups[u].contract += Number(l.quantity);
                          uomGroups[u].billed +=
                            sovQtyStats[l.id]?.billedQty ?? 0;
                          uomGroups[u].remaining +=
                            Number(l.quantity) -
                            (sovQtyStats[l.id]?.billedQty ?? 0);
                        });
                        return Object.entries(uomGroups).map(
                          ([unit, totals]) => (
                            <TableRow
                              key={unit}
                              className="text-xs text-muted-foreground"
                            >
                              <TableCell />
                              <TableCell colSpan={2}>{unit} subtotal</TableCell>
                              <TableCell className="text-right">
                                {fmtQty(totals.contract)} {unit}
                              </TableCell>
                              <TableCell />
                              <TableCell className="text-right">
                                {fmtQty(totals.billed)} {unit}
                              </TableCell>
                              <TableCell className="text-right">
                                {fmtQty(totals.remaining)} {unit}
                              </TableCell>
                              {sovViewMode === "both" && (
                                <TableCell colSpan={5} />
                              )}
                              <TableCell colSpan={3} />
                            </TableRow>
                          )
                        );
                      })()}
                  </TableFooter>
                )}
              </Table>
            </TooltipProvider>
            {/* Bar legend */}
            <div className="flex items-center gap-4 mt-2 px-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Production:</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-emerald-600 dark:bg-emerald-500" />{" "}
                Billed
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-emerald-300 dark:bg-emerald-800" />{" "}
                Complete (unbilled)
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-sky-300 dark:bg-sky-800" />{" "}
                Assigned
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-muted" /> Unassigned
              </div>
              <span className="ml-4 font-medium text-foreground">
                Invoiced:
              </span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-violet-500 dark:bg-violet-400" />{" "}
                Invoiced
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Work Orders Summary */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
          Work Orders
        </h3>
        {(() => {
          const allWos = workOrders ?? [];
          const inactiveStatuses = ["cancelled", "voided", "rejected"];
          const activeWos = allWos.filter(
            (wo: any) => !inactiveStatuses.includes(wo.status)
          );
          const inactiveWos = allWos.filter((wo: any) =>
            inactiveStatuses.includes(wo.status)
          );

          const computeWoTotal = (wo: any) => {
            const lines = (woLines ?? []).filter(
              (wol: any) =>
                wol.work_order_id === wo.id &&
                wol.status !== "voided" &&
                wol.status !== "released"
            );
            return lines.reduce(
              (s: number, wol: any) =>
                s + Number(wol.quantity) * Number(wol.contract_rate),
              0
            );
          };

          const renderRows = (wos: any[], dimmed = false) =>
            wos.map((wo: any, i: number) => {
              const woTotal = computeWoTotal(wo);
              const woLinesForWo = (woLines ?? []).filter(
                (wol: any) => wol.work_order_id === wo.id
              );
              const billed = woLinesForWo.reduce(
                (s: number, wol: any) => s + Number(wol.billed_amount),
                0
              );
              const remaining = woTotal - billed;
              return (
                <TableRow
                  key={wo.id}
                  className={cn(
                    i % 2 === 1 ? "bg-muted/30" : "",
                    dimmed && "opacity-50"
                  )}
                >
                  <TableCell className="font-mono text-sm">
                    {wo.number}
                  </TableCell>
                  <TableCell>{wo.vendors?.name ?? "—"}</TableCell>
                  <TableCell className="text-right font-medium">
                    {fmt(woTotal)}
                  </TableCell>
                  <TableCell className="text-right">{fmt(billed)}</TableCell>
                  <TableCell className="text-right">{fmt(remaining)}</TableCell>
                  <TableCell>
                    <StatusBadge status={wo.status} />
                  </TableCell>
                </TableRow>
              );
            });

          if (!allWos.length) {
            return (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No work orders yet. Assign subs from the SOV table above.
              </p>
            );
          }

          return (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO #</TableHead>
                    <TableHead>Contractor</TableHead>
                    <TableHead className="text-right">WO Total</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{renderRows(activeWos)}</TableBody>
              </Table>
              {inactiveWos.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Cancelled / Voided
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>WO #</TableHead>
                        <TableHead>Contractor</TableHead>
                        <TableHead className="text-right">WO Total</TableHead>
                        <TableHead className="text-right">Billed</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{renderRows(inactiveWos, true)}</TableBody>
                  </Table>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Assign Sub Dialog */}
      {selectedSovLine && (
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent
            className={cn(
              "overflow-y-auto",
              sovLineUnitData.hasUnits
                ? "w-[98vw] max-w-[98vw] h-[92vh] max-h-[92vh] resize"
                : "max-w-lg"
            )}
          >
            <DialogHeader>
              <DialogTitle>Assign Sub to SOV Line</DialogTitle>
              <DialogDescription>
                {selectedSovLine.description} —{" "}
                {Number(selectedSovLine.quantity).toLocaleString()}{" "}
                {selectedSovLine.unit} @ Labor{" "}
                {fmtRate(Number(selectedSovLine.labor_unit_price))}
              </DialogDescription>
            </DialogHeader>

            {(() => {
              const laborBudget =
                Number(selectedSovLine.quantity) *
                Number(selectedSovLine.labor_unit_price);
              const alreadyCommitted = woCostBySovLine[selectedSovLine.id] || 0;
              const remaining = laborBudget - alreadyCommitted;
              const rate = Number(assignForm.contract_rate) || 0;
              const hasUnits = sovLineUnitData.hasUnits;
              const qty = hasUnits
                ? unitDerivedQty
                : Number(assignForm.quantity) || 0;
              const woTotal = qty * rate;
              const laborRate = Number(selectedSovLine.labor_unit_price);
              const subRate = Number(selectedSovLine.sub_unit_price ?? 0);
              const marginDollars =
                laborRate > 0 ? (laborRate - rate) * qty : 0;
              const marginPct =
                laborRate > 0 && rate > 0
                  ? ((laborRate - rate) / laborRate) * 100
                  : 0;
              const exceedsSellRate = rate > 0 && rate >= laborRate;
              const exceedsSubRate =
                subRate > 0 && rate > subRate && !exceedsSellRate;

              const toggleUnit = (id: string) => {
                setSelectedUnitIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              };
              const selectAllAvailable = () => {
                setSelectedUnitIds(
                  new Set(sovLineUnitData.availableUnits.map((u: any) => u.id))
                );
              };
              const clearAll = () => setSelectedUnitIds(new Set());

              return (
                <div className="flex flex-col gap-4 h-full">
                  <div className="grid grid-cols-3 gap-2 text-sm bg-muted/50 rounded-md p-3">
                    <div>
                      <span className="text-muted-foreground">
                        Labor Budget:
                      </span>{" "}
                      <span className="font-medium">{fmt(laborBudget)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Committed:</span>{" "}
                      <span className="font-medium">
                        {fmt(alreadyCommitted)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Remaining:</span>{" "}
                      <span className="font-medium">{fmt(remaining)}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Subcontractor</Label>
                    <Select
                      value={assignForm.contractor_id}
                      onValueChange={(v) =>
                        setAssignForm((p) => ({ ...p, contractor_id: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcontractor" />
                      </SelectTrigger>
                      <SelectContent>
                        {subVendors.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={assignForm.description}
                      onChange={(e) =>
                        setAssignForm((p) => ({
                          ...p,
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>

                  {/* Unit selection checklist (only for unit-based SOV lines) */}
                  {hasUnits && (
                    <div className="flex flex-col flex-1 min-h-0 gap-2">
                      <div className="flex items-center justify-between">
                        <Label>
                          Units ({sovLineUnitData.availableUnits.length}{" "}
                          available, {selectedUnitIds.size} selected)
                        </Label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={selectAllAvailable}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={clearAll}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                      <div className="border rounded-md overflow-hidden flex flex-col flex-1 min-h-0">
                        <div className="grid grid-cols-[auto_1fr_80px_80px_100px] gap-0 text-xs font-medium text-muted-foreground bg-muted/50 px-3 py-1.5 border-b">
                          <span className="w-6" />
                          <span>Unit #</span>
                          <span className="text-right">Qty</span>
                          <span className="text-right">Value</span>
                          <span className="text-right">Status</span>
                        </div>
                        <div
                          className="flex-1 overflow-y-auto"
                          style={{ maxHeight: "calc(92vh - 380px)" }}
                        >
                          {/* Available units */}
                          {sovLineUnitData.availableUnits.map((u: any) => (
                            <div
                              key={u.id}
                              className="grid grid-cols-[auto_1fr_80px_80px_100px] gap-0 items-center px-3 py-1.5 hover:bg-accent/50 cursor-pointer border-b last:border-0"
                              onClick={() => toggleUnit(u.id)}
                            >
                              <Checkbox
                                checked={selectedUnitIds.has(u.id)}
                                className="mr-2 pointer-events-none"
                              />
                              <span className="font-mono text-sm">
                                {u.unit_number}
                                {u.floor_number
                                  ? ` · Fl ${u.floor_number}`
                                  : ""}
                              </span>
                              <span className="text-right text-sm">
                                {Number(u.quantity).toLocaleString()}{" "}
                                {selectedSovLine.unit}
                              </span>
                              <span className="text-right text-sm">
                                {fmt(
                                  Number(u.quantity) *
                                    Number(selectedSovLine.labor_unit_price)
                                )}
                              </span>
                              <span className="text-right">
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  Available
                                </Badge>
                              </span>
                            </div>
                          ))}
                          {/* Already assigned units */}
                          {sovLineUnitData.assignedUnits.map((u: any) => (
                            <div
                              key={u.id}
                              className="grid grid-cols-[auto_1fr_80px_80px_100px] gap-0 items-center px-3 py-1.5 opacity-60 border-b last:border-0"
                            >
                              <Checkbox checked disabled className="mr-2" />
                              <span className="font-mono text-sm">
                                {u.unit_number}
                              </span>
                              <span className="text-right text-sm">
                                {Number(u.quantity).toLocaleString()}{" "}
                                {selectedSovLine.unit}
                              </span>
                              <span className="text-right text-sm">
                                {fmt(
                                  Number(u.quantity) *
                                    Number(selectedSovLine.labor_unit_price)
                                )}
                              </span>
                              <span className="text-right">
                                <Badge
                                  variant="secondary"
                                  className="text-[10px]"
                                >
                                  {u.vendor_name}
                                </Badge>
                              </span>
                            </div>
                          ))}
                          {/* Billed units */}
                          {sovLineUnitData.billedUnits.map((u: any) => (
                            <div
                              key={u.id}
                              className="grid grid-cols-[auto_1fr_80px_80px_100px] gap-0 items-center px-3 py-1.5 opacity-40 border-b last:border-0"
                            >
                              <Checkbox checked disabled className="mr-2" />
                              <span className="font-mono text-sm line-through">
                                {u.unit_number}
                              </span>
                              <span className="text-right text-sm">
                                {Number(u.quantity).toLocaleString()}{" "}
                                {selectedSovLine.unit}
                              </span>
                              <span className="text-right text-sm">
                                {fmt(
                                  Number(u.quantity) *
                                    Number(selectedSovLine.labor_unit_price)
                                )}
                              </span>
                              <span className="text-right">
                                <Badge className="text-[10px] bg-muted text-muted-foreground">
                                  Billed
                                </Badge>
                              </span>
                            </div>
                          ))}
                          {sovLineUnitData.availableUnits.length === 0 &&
                            sovLineUnitData.assignedUnits.length === 0 &&
                            sovLineUnitData.billedUnits.length === 0 && (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No units with scope for this SOV line
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quantity / Rate row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      {hasUnits ? (
                        <Input
                          type="number"
                          value={unitDerivedQty}
                          disabled
                          className="bg-muted"
                        />
                      ) : (
                        <Input
                          type="number"
                          value={assignForm.quantity}
                          onChange={(e) =>
                            setAssignForm((p) => ({
                              ...p,
                              quantity: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Input value={assignForm.unit} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Your Rate (Labor)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={assignForm.contract_rate}
                        onChange={(e) =>
                          setAssignForm((p) => ({
                            ...p,
                            contract_rate: e.target.value,
                          }))
                        }
                        className={cn(
                          exceedsSellRate && "border-destructive",
                          exceedsSubRate && "border-yellow-500"
                        )}
                      />
                      {subRate > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Est. Sub Rate: {fmtRate(subRate)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Sell rate violation */}
                  {exceedsSellRate && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>
                        Rate must be less than sell rate ({fmtRate(laborRate)})
                        — zero-margin pricing is not allowed
                      </span>
                    </div>
                  )}

                  {/* Sub rate override warning */}
                  {exceedsSubRate && (
                    <div className="space-y-2 p-3 rounded-md bg-yellow-50/50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span>
                          Rate exceeds estimated sub rate ({fmtRate(subRate)}) —
                          variance reason required
                        </span>
                        {!can("approve_rate_overrides") && !isAdmin && (
                          <Badge variant="destructive" className="text-[10px]">
                            No override authority
                          </Badge>
                        )}
                      </div>
                      <Textarea
                        placeholder="Explain why this rate exceeds the estimated sub cost…"
                        className="h-16 text-sm"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                      />
                    </div>
                  )}

                  {rate > 0 && !exceedsSellRate && (
                    <div
                      className={cn(
                        "rounded-md p-3 text-sm",
                        marginPct < 10
                          ? "bg-destructive/10 border border-destructive/30"
                          : "bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800"
                      )}
                    >
                      <span className="font-medium">Company margin: </span>
                      <span
                        className={cn(
                          "font-semibold",
                          marginPct >= 20
                            ? "text-green-600"
                            : marginPct >= 10
                            ? "text-yellow-600"
                            : "text-destructive"
                        )}
                      >
                        {fmt(marginDollars)} ({pct(marginPct)})
                      </span>
                      {woTotal > remaining && (
                        <span className="text-destructive ml-2 font-medium">
                          ⚠ Exceeds remaining labor budget ({fmt(remaining)})
                        </span>
                      )}
                    </div>
                  )}

                  <div className="text-sm text-muted-foreground">
                    WO Total:{" "}
                    <span className="font-semibold text-foreground">
                      {fmt(woTotal)}
                    </span>
                    {hasUnits && selectedUnitIds.size > 0 && (
                      <span className="ml-2">
                        ({selectedUnitIds.size} units × {fmtRate(rate)})
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(false)}>
                Cancel
              </Button>
              <Button disabled={busy} onClick={handleAssignSub}>
                {busy ? "Creating…" : "Create Work Order"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* SOV Edit Dialog */}
      {editingSovLine && (
        <SovEditDialog
          open={!!editingSovLine}
          onOpenChange={(o) => {
            if (!o) setEditingSovLine(null);
          }}
          sovLine={editingSovLine}
          projectId={projectId}
          hasBilling={false}
          hasWorkOrders={!!(woCostBySovLine[editingSovLine.id] > 0)}
          onComplete={refreshAll}
        />
      )}
    </div>
  );
};

// ===== Column definitions for SOV table =====
type ColumnKey =
  | "description"
  | "line_type"
  | "quantity"
  | "unit"
  | "labor_unit_price"
  | "material_unit_price"
  | "sub_unit_price"
  | "total_rate"
  | "line_total";

const ALL_COLUMNS: { key: ColumnKey; label: string; defaultWidth: number }[] = [
  { key: "description", label: "Description", defaultWidth: 300 },
  { key: "line_type", label: "Type", defaultWidth: 120 },
  { key: "quantity", label: "Qty", defaultWidth: 150 },
  { key: "unit", label: "Unit", defaultWidth: 110 },
  { key: "labor_unit_price", label: "Labor Price", defaultWidth: 130 },
  { key: "material_unit_price", label: "Mat Price", defaultWidth: 130 },
  { key: "sub_unit_price", label: "Sub Rate", defaultWidth: 120 },
  { key: "total_rate", label: "Total Rate", defaultWidth: 120 },
  { key: "line_total", label: "Line Total", defaultWidth: 130 },
];

const UNIT_OPTIONS = ["SF", "LF", "EA", "HR", "LS"];

const DEFAULT_COLUMN_ORDER: ColumnKey[] = ALL_COLUMNS.map((c) => c.key);

// ===== Create Contract Dialog Component =====
const CreateContractDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  lineItems,
  addLineItem,
  removeLineItem,
  updateLineItem,
  formLaborTotal,
  formMaterialTotal,
  formTotal,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  form: { name: string; description: string };
  setForm: (fn: (p: any) => any) => void;
  lineItems: SovLineForm[];
  addLineItem: () => void;
  removeLineItem: (i: number) => void;
  updateLineItem: (i: number, f: keyof SovLineForm, v: string) => void;
  formLaborTotal: number;
  formMaterialTotal: number;
  formTotal: number;
  busy: boolean;
  onSubmit: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [columnOrder, setColumnOrder] =
    useState<ColumnKey[]>(DEFAULT_COLUMN_ORDER);
  const [dragCol, setDragCol] = useState<ColumnKey | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<ColumnKey, number>>(
    () =>
      Object.fromEntries(
        ALL_COLUMNS.map((c) => [c.key, c.defaultWidth])
      ) as Record<ColumnKey, number>
  );
  const [customUnits, setCustomUnits] = useState<string[]>([]);
  const resizingRef = useRef<{
    key: ColumnKey;
    startX: number;
    startW: number;
  } | null>(null);

  const orderedColumns = useMemo(
    () => columnOrder.map((key) => ALL_COLUMNS.find((c) => c.key === key)!),
    [columnOrder]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, key: ColumnKey) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = {
        key,
        startX: e.clientX,
        startW: columnWidths[key],
      };
      const onMove = (ev: MouseEvent) => {
        if (!resizingRef.current) return;
        const diff = ev.clientX - resizingRef.current.startX;
        const newW = Math.max(80, resizingRef.current.startW + diff);
        setColumnWidths((prev) => ({
          ...prev,
          [resizingRef.current!.key]: newW,
        }));
      };
      const onUp = () => {
        resizingRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [columnWidths]
  );

  const allUnits = useMemo(
    () => [...UNIT_OPTIONS, ...customUnits],
    [customUnits]
  );
  const [addingUnit, setAddingUnit] = useState(false);
  const [newUnitValue, setNewUnitValue] = useState("");

  const handleDragStart = (key: ColumnKey) => setDragCol(key);
  const handleDragOver = (e: React.DragEvent, targetKey: ColumnKey) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetKey) return;
    setColumnOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(dragCol);
      const toIdx = next.indexOf(targetKey);
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragCol);
      return next;
    });
  };
  const handleDragEnd = () => setDragCol(null);

  const renderCell = (line: SovLineForm, idx: number, col: ColumnKey) => {
    const laborUp = Number(line.labor_unit_price);
    const matUp = Number(line.material_unit_price);
    const totalUp = laborUp + matUp;
    const lineTotal = Number(line.quantity) * totalUp;
    const showLabor = line.line_type === "labor" || line.line_type === "both";
    const showMat = line.line_type === "materials" || line.line_type === "both";

    switch (col) {
      case "description":
        return (
          <Textarea
            value={line.description}
            onChange={(e) => updateLineItem(idx, "description", e.target.value)}
            placeholder="Description"
            className="min-h-[32px] h-8 focus:h-20 transition-all duration-150 resize-y text-sm py-1 px-2 min-w-[200px]"
          />
        );
      case "line_type":
        return (
          <Select
            value={line.line_type}
            onValueChange={(v) => updateLineItem(idx, "line_type", v)}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="labor">Labor</SelectItem>
              <SelectItem value="materials">Materials</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        );
      case "quantity":
        return (
          <Input
            type="number"
            value={line.quantity}
            onChange={(e) => updateLineItem(idx, "quantity", e.target.value)}
            className="h-8 min-w-[100px]"
          />
        );
      case "unit":
        return (
          <div className="flex items-center gap-1">
            {addingUnit ? (
              <Input
                value={newUnitValue}
                onChange={(e) => setNewUnitValue(e.target.value.toUpperCase())}
                placeholder="e.g. SY"
                className="h-8 w-16"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newUnitValue.trim()) {
                    const u = newUnitValue.trim();
                    if (!allUnits.includes(u))
                      setCustomUnits((prev) => [...prev, u]);
                    updateLineItem(idx, "unit", u);
                    setNewUnitValue("");
                    setAddingUnit(false);
                  }
                  if (e.key === "Escape") {
                    setAddingUnit(false);
                    setNewUnitValue("");
                  }
                }}
              />
            ) : (
              <>
                <Select
                  value={line.unit}
                  onValueChange={(v) => updateLineItem(idx, "unit", v)}
                >
                  <SelectTrigger className="h-8 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allUnits.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => setAddingUnit(true)}
                  title="Add unit"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        );
      case "labor_unit_price":
        return showLabor ? (
          <Input
            type="number"
            step="0.01"
            value={line.labor_unit_price}
            onChange={(e) =>
              updateLineItem(idx, "labor_unit_price", e.target.value)
            }
            className="h-8"
          />
        ) : (
          <span className="text-xs text-muted-foreground px-2">—</span>
        );
      case "material_unit_price":
        return showMat ? (
          <Input
            type="number"
            step="0.01"
            value={line.material_unit_price}
            onChange={(e) =>
              updateLineItem(idx, "material_unit_price", e.target.value)
            }
            className="h-8"
          />
        ) : (
          <span className="text-xs text-muted-foreground px-2">—</span>
        );
      case "sub_unit_price":
        return showLabor ? (
          <Input
            type="number"
            step="0.01"
            value={line.sub_unit_price}
            onChange={(e) =>
              updateLineItem(idx, "sub_unit_price", e.target.value)
            }
            className="h-8"
            placeholder="0.00"
          />
        ) : (
          <span className="text-xs text-muted-foreground px-2">—</span>
        );
      case "total_rate":
        return (
          <span className="text-muted-foreground font-medium">
            {fmtRate(totalUp)}
          </span>
        );
      case "line_total":
        return <span className="font-semibold">{fmt(lineTotal)}</span>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overflow-y-auto transition-all duration-200",
          expanded
            ? "max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh]"
            : "max-w-5xl max-h-[85vh]"
        )}
      >
        <DialogHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <DialogTitle>Create Contract</DialogTitle>
            <DialogDescription>
              Define the contract and its Schedule of Values.
            </DialogDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 mt-0"
            onClick={() => setExpanded((e) => !e)}
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contract Name</Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((p: any) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g. Prime Contract"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={form.description}
                onChange={(e) =>
                  setForm((p: any) => ({ ...p, description: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>SOV Line Items</Label>
              <span className="text-xs text-muted-foreground">
                Drag headers to reorder · Drag edges to resize
              </span>
            </div>
            <div className="overflow-x-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    {orderedColumns.map((col) => (
                      <TableHead
                        key={col.key}
                        className={cn(
                          "cursor-grab select-none relative group",
                          dragCol === col.key && "opacity-50 bg-accent"
                        )}
                        style={{ width: columnWidths[col.key], minWidth: 80 }}
                        draggable
                        onDragStart={() => handleDragStart(col.key)}
                        onDragOver={(e) => handleDragOver(e, col.key)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="flex items-center gap-1 pr-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="truncate">{col.label}</span>
                        </div>
                        {/* Resize handle */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/20 z-10"
                          onMouseDown={(e) => handleResizeStart(e, col.key)}
                          draggable={false}
                        />
                      </TableHead>
                    ))}
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs text-muted-foreground">
                        {idx + 1}
                      </TableCell>
                      {orderedColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          style={{ width: columnWidths[col.key], minWidth: 80 }}
                          className={
                            col.key === "total_rate" || col.key === "line_total"
                              ? "text-right"
                              : ""
                          }
                        >
                          {renderCell(line, idx, col.key)}
                        </TableCell>
                      ))}
                      <TableCell>
                        {lineItems.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeLineItem(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button variant="outline" size="sm" onClick={addLineItem}>
              <Plus className="mr-1 h-3 w-3" />
              Add Row
            </Button>
          </div>

          <div className="flex justify-end gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Labor Total:</span>{" "}
              <span className="font-semibold">{fmt(formLaborTotal)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Material Total:</span>{" "}
              <span className="font-semibold">{fmt(formMaterialTotal)}</span>
            </div>
            <div className="text-lg font-semibold">Total: {fmt(formTotal)}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={onSubmit}>
            {busy ? "Creating…" : "Create Contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
